import { FeaturePageLayout } from '../../components/features/FeaturePageLayout';
import { AlertesMockup, DashboardMockup } from '../../components/features/ProductMockups';

const RELATED = [
  { label: 'Prévision de trésorerie', href: '/fonctionnalites/prevision-tresorerie' },
  { label: 'Stress test', href: '/fonctionnalites/stress-test-tresorerie' },
  { label: 'Suivi des subventions', href: '/fonctionnalites/suivi-subventions' },
  { label: 'Tableau de bord', href: '/fonctionnalites/tableau-bord-temps-reel' },
];

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function AlertesTresoreriePage({ onNavigate, onSignIn, onSignUp }: Props) {
  return (
    <FeaturePageLayout
      seo={{
        title: 'Alertes de trésorerie — Soyez alerté avant la crise | TrezoOne',
        description: 'Configurez des alertes personnalisées sur votre trésorerie : seuil bas, retard de flux, tension prévisionnelle. TrezoOne vous prévient avant qu\'il ne soit trop tard.',
        canonical: '/fonctionnalites/alertes-tresorerie',
        keywords: 'alertes trésorerie, notification solde bas, alerte retard paiement, surveillance trésorerie, seuil alerte',
      }}
      activeNavId="alertes"
      hero={{
        tag: 'Alertes de trésorerie',
        title: 'Soyez alerté avant que la situation ne devienne critique.',
        subtitle: 'Configurez des seuils personnalisés et recevez une alerte dès qu\'un risque est détecté : solde trop bas, retard de paiement, tension prévisionnelle. Ne découvrez plus les problèmes trop tard.',
        mockup: <AlertesMockup />,
      }}
      problem={{
        title: 'Vous surveillez votre trésorerie manuellement — et vous ratez les signaux d\'alarme',
        points: [
          'Vous consultez vos comptes de façon irrégulière et découvrez les problèmes trop tard.',
          'Un découvert évitable arrive parce que personne n\'a surveillé le solde à temps.',
          'Vous passez trop de temps à vérifier l\'état de vos flux manuellement.',
          'Vous n\'avez aucune visibilité sur les tensions qui arrivent dans les semaines à venir.',
        ],
      }}
      steps={[
        { num: 1, title: 'Configurez vos seuils', desc: 'Définissez le solde minimum en dessous duquel vous souhaitez être alerté. Personnalisez par compte.' },
        { num: 2, title: 'Définissez vos règles de suivi', desc: 'Configurez des alertes sur les retards de flux : subventions en retard, factures impayées, décaissements inhabituels.' },
        { num: 3, title: 'Recevez vos notifications', desc: 'TrezoOne génère une alerte visuelle dans votre tableau de bord dès qu\'une condition est remplie.' },
        { num: 4, title: 'Agissez au bon moment', desc: 'Chaque alerte est accompagnée d\'une suggestion d\'action pour résoudre ou anticiper le problème.' },
      ]}
      screens={[<AlertesMockup />, <DashboardMockup />]}
      useCases={[
        { audience: 'Associations', benefit: 'Soyez alerté dès qu\'une subvention est en retard ou que votre solde passe sous un seuil critique.' },
        { audience: 'Trésorier(e)', benefit: 'Surveillez plusieurs comptes sans y passer des heures chaque semaine.' },
        { audience: 'Dirigeant', benefit: 'Recevez uniquement les alertes importantes, sans être noyé dans les détails.' },
        { audience: 'PME', benefit: 'Soyez prévenu avant un découvert sur votre compte courant professionnel.' },
        { audience: 'Comptable', benefit: 'Assurez une veille continue sur les soldes sans processus manuels répétitifs.' },
        { audience: 'RAF', benefit: 'Centralisez la surveillance de plusieurs entités depuis une seule interface.' },
      ]}
      benefits={[
        'Ne plus jamais découvrir un problème de trésorerie trop tard',
        'Réduire drastiquement le temps passé à surveiller les comptes manuellement',
        'Agir avant la crise, pas pendant',
        'Configurer des alertes adaptées à votre saisonnalité et vos cycles financiers',
        'Avoir une vue prioritisée des risques actifs',
        'Déléguer la surveillance sans perdre le contrôle',
      ]}
      faq={[
        { q: 'Peut-on configurer plusieurs seuils ?', a: 'Oui. Vous pouvez définir des seuils d\'alerte différents par compte bancaire et par type de flux.' },
        { q: 'Les alertes sont-elles envoyées par email ?', a: 'Les alertes sont visibles dans votre tableau de bord TrezoOne. Des notifications par email peuvent être configurées selon votre pack.' },
        { q: 'Peut-on désactiver certaines alertes temporairement ?', a: 'Oui. Chaque règle d\'alerte peut être activée ou désactivée individuellement, sans la supprimer.' },
        { q: 'Les alertes sont-elles basées sur les données en temps réel ?', a: 'Les alertes sont basées sur les données importées dans TrezoOne. Elles sont recalculées à chaque import de nouvelles transactions.' },
      ]}
      relatedLinks={RELATED}
      onNavigate={onNavigate}
      onSignIn={onSignIn}
      onSignUp={onSignUp}
    />
  );
}
